import API, { License, LicenseCreateRequest, LicenseStatistics, LicenseUpdateRequest } from "@/api/api";
import { toast } from "@/lib/instrumented-toast";
import { create } from "zustand";

interface LicenseStoreState {
  licenses: License[];
  statistics: LicenseStatistics | null;
  isLoading: boolean;

  fetchLicenses: (orgId?: string, includeInactive?: boolean) => Promise<void>;
  generateLicense: (request: LicenseCreateRequest) => Promise<License>;
  updateLicense: (id: number, request: LicenseUpdateRequest) => Promise<License>;
  fetchStatistics: (orgId?: string) => Promise<void>;
  validateLicense: (key: string, holder: string) => Promise<boolean>;
}

const api = new API();

const useLicenseStoreBase = create<LicenseStoreState>()((set) => ({
  licenses: [],
  statistics: null,
  isLoading: false,

  fetchLicenses: async (orgId?: string, includeInactive = false) => {
    set({ isLoading: true });
    try {
      const response = await api.listLicenses({
        org_id: orgId,
        include_inactive: includeInactive,
        page: 1,
        page_size: 100,
      });
      set({ licenses: response.licenses, isLoading: false });
    } catch (error) {
      console.error("fetchLicenses error:", error);
      let errorMessage = "Failed to fetch licenses";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "object" && error !== null && "message" in error) {
        errorMessage = String(error.message);
      } else if (typeof error === "string") {
        errorMessage = error;
      }
      toast.error(errorMessage);
      set({ isLoading: false });
    }
  },

  generateLicense: async (request: LicenseCreateRequest) => {
    set({ isLoading: true });
    try {
      const license = await api.generateLicense(request);
      set((state) => ({
        licenses: [license, ...state.licenses],
        isLoading: false,
      }));

      const { fetchStatistics } = useLicenseStoreBase.getState();
      await fetchStatistics(request.organization_id);

      return license;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to generate license";
      toast.error(errorMessage);
      set({ isLoading: false });
      throw error;
    }
  },

  updateLicense: async (id: number, request: LicenseUpdateRequest) => {
    set({ isLoading: true });
    try {
      const updatedLicense = await api.updateLicense(id, request);
      set((state) => ({
        licenses: state.licenses.map((lic) => (lic.id === id ? updatedLicense : lic)),
        isLoading: false,
      }));

      const { fetchStatistics } = useLicenseStoreBase.getState();
      await fetchStatistics(updatedLicense.organization_id);

      return updatedLicense;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update license";
      toast.error(errorMessage);
      set({ isLoading: false });
      throw error;
    }
  },

  fetchStatistics: async (orgId?: string) => {
    set({ isLoading: true });
    try {
      const statistics = await api.getLicenseStatistics(orgId);
      set({ statistics, isLoading: false });
    } catch (error) {
      console.error("fetchStatistics error:", error);
      let errorMessage = "Failed to fetch statistics";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "object" && error !== null && "message" in error) {
        errorMessage = String(error.message);
      } else if (typeof error === "string") {
        errorMessage = error;
      }
      toast.error(errorMessage);
      set({ isLoading: false });
    }
  },

  validateLicense: async (key: string, holder: string) => {
    set({ isLoading: true });
    try {
      const result = await api.validateLicense(key, holder);
      set({ isLoading: false });
      return result.is_valid && result.is_active;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to validate license";
      toast.error(errorMessage);
      set({ isLoading: false });
      return false;
    }
  },
}));

// Export the store directly without createSelectors wrapper
export const useLicenseStore = useLicenseStoreBase;
